// Groups the pallets on a receipt by what happens next (F07 UF-06).
// "To place" = received, not yet in a room; "In cold" = in a room;
// "Gone" = retipped, dispatched, voided. The first two hold only pallets
// the selection bar accepts, so Select all can tick the whole group.
export interface GroupablePallet {
  pallet_id: string
  status: string
  deleted_at: string | null
}

export type ReceiptPalletGroup = 'toPlace' | 'inCold' | 'gone'

export function receiptPalletGroup(p: GroupablePallet): ReceiptPalletGroup {
  if (p.deleted_at) return 'gone'
  if (p.status === 'IN_PRODUCTION') return 'toPlace'
  if (p.status === 'IN_COLDROOM') return 'inCold'
  return 'gone'
}

export function groupReceiptPallets<T extends GroupablePallet>(
  pallets: readonly T[],
): Record<ReceiptPalletGroup, T[]> {
  const out: Record<ReceiptPalletGroup, T[]> = { toPlace: [], inCold: [], gone: [] }
  for (const p of pallets) out[receiptPalletGroup(p)].push(p)
  return out
}

// What the receipt's edit pencil may change on one pallet (mig 20261112500000).
// 'full' while it is received or in a room; 'details' once the strip itself
// dispatched it (Gone · Retipped): its cartons are in bins, so what it HELD is
// locked and only what it IS stays correctable. null otherwise: a pallet that
// left on a truck, or one voided / stripped before it was placed. `retipped`
// is isRetippedDispatch's answer (it needs the strip times the page reads).
export type PalletEditMode = 'full' | 'details' | null

export function palletEditMode(p: GroupablePallet, retipped: boolean): PalletEditMode {
  if (p.deleted_at) return null
  if (p.status === 'IN_PRODUCTION' || p.status === 'IN_COLDROOM') return 'full'
  return retipped && p.status === 'DISPATCHED' ? 'details' : null
}

// The fields a 'details' edit leaves read-only — the same set
// update_received_pallet refuses to change on a retipped pallet.
export const RETIPPED_LOCKED_FIELDS = ['sscc', 'commodity_code', 'carton_count', 'gross_kg', 'net_kg', 'pack_date'] as const

// The receipt opened from a pallet-number search names that pallet. When it is
// under Gone, the group opens on it — closed by default, it was where Rika's
// 94464 "disappeared" (20 Oct 2026).
export function focusIsGone(pallets: readonly GroupablePallet[], focusId: string | null): boolean {
  if (!focusId) return false
  const p = pallets.find(x => x.pallet_id === focusId)
  return !!p && receiptPalletGroup(p) === 'gone'
}

// Select all: tick the whole group, or clear it when every one is ticked.
export function toggleGroupSelection(
  selected: ReadonlySet<string>,
  ids: readonly string[],
): Set<string> {
  const next = new Set(selected)
  const allOn = ids.length > 0 && ids.every(id => next.has(id))
  for (const id of ids) {
    if (allOn) next.delete(id)
    else next.add(id)
  }
  return next
}

// UF-07: the room a placement form opens on. The room last placed into, if it
// is still active; else the first room that is not the cross-dock.
export function startRoomId(
  rooms: readonly { coldroom_id: string; code: string; name: string }[],
  lastId: string,
): string {
  if (lastId && rooms.some(r => r.coldroom_id === lastId)) return lastId
  const xd = (r: { code: string; name: string }) =>
    /^xd$|cross[\s-]?dock/i.test(r.code) || /cross[\s-]?dock/i.test(r.name)
  return (rooms.find(r => !xd(r)) ?? rooms[0])?.coldroom_id ?? ''
}

// UF-08: a pallet loaded on a truck (pallets.loading_mission_id set, still
// live and not yet dispatched). The receipt then offers no tick, Retip or
// Void: a strip or void would soft-delete it and the truck would silently
// lose a pallet. UI only; the server guard is UF-20.
export interface TruckPallet extends GroupablePallet {
  loading_mission_id: string | null
}

export function isOnTruck(p: Pick<TruckPallet, 'loading_mission_id' | 'deleted_at' | 'status'>): boolean {
  return !!p.loading_mission_id && !p.deleted_at && p.status !== 'DISPATCHED'
}

// The receipt's tick, Select all, Create run and Add to run accept a pallet
// that is received, live and not on a truck.
export function isSelectablePallet(p: TruckPallet): boolean {
  return receiptPalletGroup(p) !== 'gone' && !isOnTruck(p)
}

// Void only where the server can succeed: live, not dispatched, not on a truck.
export function canVoidPallet(p: TruckPallet): boolean {
  return !p.deleted_at && p.status !== 'DISPATCHED' && !isOnTruck(p)
}

// UF-07: the pallet to open after placing `placedId`: the next one in the To
// place list that is not on a truck, else the one before it; null when none.
export function nextPalletToPlace<T extends TruckPallet>(pallets: readonly T[], placedId: string): T | null {
  const list = groupReceiptPallets(pallets).toPlace
  const at = list.findIndex(x => x.pallet_id === placedId)
  const queue = list.filter(x => x.pallet_id !== placedId && !isOnTruck(x))
  if (queue.length === 0) return null
  const before = list.slice(0, Math.max(at, 0)).filter(x => x.pallet_id !== placedId && !isOnTruck(x)).length
  return queue[Math.min(before, queue.length - 1)]
}

// Select all ticks only what the single tick would: no truck, no gone.
export function selectableIds<T extends TruckPallet>(rows: readonly T[]): string[] {
  return rows.filter(isSelectablePallet).map(r => r.pallet_id)
}
